import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SidebarHeader } from "@repo/ui/primitives/sidebar";
import { useAuthStore } from "@/store";
import { useTenantStore } from "@/store/tenant.store";
import { useTailorabilityStore } from "@/store/tailorability.store";
import { tailorabilityIntegration } from "@/lib/api/client";
import { useBrandMarkAssets } from "@/components/branding/use-brand-mark-assets.js";
import { cn } from "@/lib/utils";

function resolveTenantName(user, selectedTenantId) {
  if (!user?.memberships?.length) return null;
  const membership = user.memberships.find((m) => {
    const tId = m?.tenant?.id || m?.tenant?._id || m?.tenant;
    return selectedTenantId ? String(tId) === String(selectedTenantId) : true;
  }) || user.memberships[0];
  return membership?.tenant?.name || membership?.tenantName || null;
}

export function SidebarHeaderSection({ isCollapsed, navigate }) {
  const { t } = useTranslation("sidebar");
  const { user } = useAuthStore();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const branding = useTailorabilityStore((s) => s.branding);
  const { logoSrc, defaultLogo, defaultText, productLabel, useWordmarkImage } = useBrandMarkAssets();

  useEffect(() => {
    if (!branding && user) {
      tailorabilityIntegration.ensureBrandingLoaded().catch(() => {});
    }
  }, [branding, user]);

  const tenantName = resolveTenantName(user, selectedTenantId);

  return (
    <SidebarHeader className="h-[var(--ds-header-height)] min-h-[var(--ds-header-height)] shrink-0 border-b border-sidebar-border bg-sidebar p-0">
      <button
        type="button"
        onClick={() => navigate("/home")}
        aria-label={t("nav.home", { defaultValue: "Home" })}
        className={cn(
"flex h-full w-full cursor-pointer items-center justify-center rounded-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring motion-reduce:transition-none",
          isCollapsed ? "px-0" : "px-2",
        )}
      >
        <div className="flex min-w-0 max-w-full flex-col items-center justify-center text-center">
          <div className="flex min-w-0 max-w-full items-center justify-center gap-2">
            <img
              src={logoSrc}
              alt=""
              className="sgrc-logo-mark relative z-[1] size-8 shrink-0 object-contain"
              onError={(e) => {
                e.currentTarget.src = defaultLogo;
              }}
            />
            {!isCollapsed && (useWordmarkImage ? (
              <img
                src={defaultText}
                alt="sGRC"
                className="sgrc-logo-text relative z-[1] h-[1.375rem] w-auto max-w-[5.5rem] object-contain"
              />
            ) : (
              <span className="min-w-0 truncate text-sm font-semibold leading-tight text-sidebar-foreground">
                {productLabel}
              </span>
            ))}
          </div>
          {!isCollapsed && tenantName && (
            <span className="mt-0.5 max-w-full truncate text-[10px] font-medium leading-tight text-sidebar-foreground/50">
              {tenantName}
            </span>
          )}
        </div>
      </button>
    </SidebarHeader>
  );
}
